import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../config/firebase";
import { RootState } from "../setup/redux/Store";
import {
  SubscriptionTier,
  FeatureKey,
  TIER_FEATURES,
  TIER_LIMITS,
} from "../config/subscriptionConfig";

export function useSubscription() {
  const user = useSelector((state: RootState) => state.auth.user);
  const uid = user?.uid;

  // Real-time Firestore listener — updated when Stripe webhook writes subscription tier
  const [firestoreTier, setFirestoreTier] = useState<SubscriptionTier | null>(null);
  const [firestoreStatus, setFirestoreStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) return;
    const userRef = doc(db, "users", uid);
    const unsubscribe = onSnapshot(userRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.subscriptionTier) setFirestoreTier(data.subscriptionTier as SubscriptionTier);
        if (data.subscriptionStatus) setFirestoreStatus(data.subscriptionStatus as string);
      }
    });
    return unsubscribe;
  }, [uid]);

  // Prefer Firestore values (real-time, updated by Stripe webhook) over Redux auth state
  const tier: SubscriptionTier = firestoreTier ?? user?.subscriptionTier ?? "free";
  const status: string = firestoreStatus ?? user?.subscriptionStatus ?? "active";

  const isActive = status === "active";

  const canUseFeature = (feature: FeatureKey): boolean => {
    if (!isActive && tier !== "free") return false;
    return TIER_FEATURES[tier].includes(feature);
  };

  const isProOrAbove = tier === "pro" || tier === "business" || tier === "agency";
  const isBusiness = tier === "business" || tier === "agency";
  const isAgency = tier === "agency";
  const projectLimit = TIER_LIMITS[tier].maxProjects;

  return {
    tier,
    status,
    isActive,
    canUseFeature,
    isProOrAbove,
    isBusiness,
    isAgency,
    projectLimit,
  };
}
